import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';

import { Mapper } from '@shared/helpers/mapper';
import { SecurityMetadata } from '../../abstractions/security-metadata';
import { SecurityReviewUpdate } from '../../abstractions/security-review-update';
import { SecurityTaxResultItemForm } from '../../models/edit-tax-result-approval/security-tax-result-item-form';
import { SecurityTaxResultItemReview } from '../../models/edit-tax-result-approval/security-tax-result-item-review';
import { TaxResultItemField } from '../../models/edit-tax-result-approval/tax-result-item-field';
import { TaxResultSessionService } from '../../services/tax-result-session.service';

@Component({
  standalone: false,
  selector: 'gstar-rates-manager',
  templateUrl: './rates-manager.component.html',
  styleUrls: ['./rates-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RatesManagerComponent implements OnDestroy, OnInit {
  private readonly taxResultSession = inject(TaxResultSessionService);

  isReadonly = input.required<boolean>();
  securityTaxResultItem = model.required<SecurityTaxResultItemReview>();
  sortedRateItems = computed<SecurityTaxResultItemForm[]>(() => {
    return this.getSortedRateItems(this.securityTaxResultItem().items());
  });

  explanation!: TaxResultItemField;

  ngOnInit(): void {
    this.fillExplanationByGroup(this.securityTaxResultItem());
  }

  ngOnDestroy(): void {
    this.taxResultSession.cleanCache();
  }

  add({
    securityId,
    transactionTypeId,
    taxResultItemTypeId,
  }: SecurityMetadata): void {
    const newItem = Mapper.map({}, SecurityTaxResultItemForm, [
      {
        uuid: crypto.randomUUID(),
        rate: Mapper.map({}, TaxResultItemField, [
          {
            uuid: crypto.randomUUID(),
            value: (): WritableSignal<string> => signal(''),
            metadata: {
              securityId,
              taxResultItemTypeId,
              transactionTypeId,
            },
          },
        ]),
        questionText: '',
        metadata: {
          securityId,
          taxResultItemTypeId,
          transactionTypeId,
        },
        explanation: Mapper.map({}, TaxResultItemField, [
          {
            uuid: crypto.randomUUID(),
            value: (): WritableSignal<string> =>
              signal(this.getExplanationValueOrDefault()),
            metadata: {
              securityId,
              taxResultItemTypeId,
              transactionTypeId,
            },
          },
        ]),
      },
    ]);

    this.securityTaxResultItem().items.update(items => [...items, newItem]);

    this.securityTaxResultItem().items.set(
      this.clone(this.securityTaxResultItem()).items()
    );

    this.taxResultSession.add(newItem);
  }

  deleteRate(rateToDelete: SecurityTaxResultItemForm): void {
    const items = this.securityTaxResultItem()
      .items()
      .filter((item: SecurityTaxResultItemForm) => {
        return item.uuid !== rateToDelete.uuid;
      });
    this.securityTaxResultItem().items.set(items);
    this.securityTaxResultItem().items.set(
      this.clone(this.securityTaxResultItem()).items()
    );
    this.taxResultSession.delete(rateToDelete);
  }

  updateHandler(
    update: SecurityReviewUpdate,
    itemForm: SecurityTaxResultItemForm
  ): void {
    itemForm.rate!.hasDuplicatedValue.set(
      this.hasDuplicatedValue(
        this.securityTaxResultItem().items(),
        itemForm.rate!
      )
    );

    this.securityTaxResultItem().items.update(
      (items: SecurityTaxResultItemForm[]): SecurityTaxResultItemForm[] => {
        return items.map(
          (item: SecurityTaxResultItemForm): SecurityTaxResultItemForm => {
            if (item.uuid === itemForm.uuid) {
              item.rate!.value.set(itemForm.rate!.value());
              item.explanation?.value.set(this.getExplanationValueOrDefault());
            }
            return item;
          }
        );
      }
    );
    const clone = this.clone(this.securityTaxResultItem());
    this.securityTaxResultItem().items.set(clone.items());
    if (itemForm.rate!.metadata.securityTaxResultItemId) {
      this.taxResultSession.updateSecurity.next(update);
    } else {
      this.taxResultSession.update(itemForm);
    }
  }

  private clone(
    source: SecurityTaxResultItemReview
  ): SecurityTaxResultItemReview {
    return Mapper.map(source, SecurityTaxResultItemReview, [
      'securityTaxResultItemId',
      'taxResultTypeId',
      'title',
      'initialSize',
      {
        items: () => signal(source.items()),
      },
    ]);
  }

  private fillExplanationByGroup(review: SecurityTaxResultItemReview): void {
    const securityTaxResultForm = review
      .items()
      .find(item => item.explanation?.value());

    if (securityTaxResultForm) {
      this.explanation = securityTaxResultForm.explanation!;

      review
        .items()
        .forEach((securityTaxResultItem: SecurityTaxResultItemForm) => {
          securityTaxResultItem.explanation = this.explanation;
        });
    }
  }

  private hasDuplicatedValue(
    items: SecurityTaxResultItemForm[],
    itemField: TaxResultItemField
  ): boolean {
    return (
      items.filter(
        (item: SecurityTaxResultItemForm) =>
          item.rate?.value() &&
          parseFloat(item.rate?.value()) === parseFloat(itemField.value())
      ).length > 1
    );
  }

  private getExplanationValueOrDefault(): string {
    const itemForm = this.securityTaxResultItem()
      .items()
      .find(item => item.explanation);
    return itemForm ? itemForm.explanation!.value() : '';
  }

  private getSortedRateItems(
    items: SecurityTaxResultItemForm[]
  ): SecurityTaxResultItemForm[] {
    return items
      .filter((item: SecurityTaxResultItemForm) => item.rate !== undefined)
      .sort((a, b) => {
        const first = parseFloat(a.rate!.value());
        const second = parseFloat(b.rate!.value());
        if (first < second || (!isNaN(first) && isNaN(second))) {
          return -1;
        }
        if (first === second || (isNaN(first) && isNaN(second))) {
          return 0;
        }
        return 1;
      });
  }
}
